import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { fmtErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Badge, PRIORITY_STYLE, STATUS_STYLE } from "../../components/DeskLayout";
import { StatCard } from "./DeskDashboard";
import { ArrowLeft, Ticket, CircleDot, CalendarClock, Mail, Phone, Building2, MapPin, Briefcase, Pencil, Trash2, StickyNote, PhoneCall, Video } from "lucide-react";
import { ContactEditor } from "./Crm";

const I_ICON = { note: StickyNote, call: PhoneCall, email: Mail, meeting: Video };
const inputCls = "bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none";

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2.5 text-sm py-1.5">
    <Icon size={14} className="text-slate-400 mt-0.5 shrink-0" />
    <span className="text-slate-400 w-24 shrink-0 text-xs pt-0.5">{label}</span>
    <span className="font-medium break-all">{value || <span className="text-slate-300">—</span>}</span>
  </div>
);

export default function CrmContact() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "hd_admin" || user?.role === "super_admin";
  const [c, setC] = useState(null);
  const [editor, setEditor] = useState(null);
  const [note, setNote] = useState({ type: "note", body: "" });

  const load = useCallback(() => {
    api.get(`/helpdesk/crm/contacts/${id}`).then((r) => setC(r.data)).catch((e) => { toast.error(fmtErr(e)); navigate("/desk/crm"); });
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const addInteraction = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/helpdesk/crm/contacts/${id}/interactions`, note);
      setNote({ type: "note", body: "" });
      toast.success("Logged");
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const removeInteraction = async (i) => {
    await api.delete(`/helpdesk/crm/contacts/${id}/interactions/${i.id}`).catch((e) => toast.error(fmtErr(e)));
    load();
  };

  const removeContact = async () => {
    if (!window.confirm(`Delete contact ${c.name}? Tickets are kept.`)) return;
    try { await api.delete(`/helpdesk/crm/contacts/${id}`); toast.success("Contact deleted"); navigate("/desk/crm"); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  if (!c) return <p className="text-sm text-slate-400" data-testid="crm-contact-loading">Loading contact...</p>;

  return (
    <div data-testid="crm-contact-page">
      <Link to="/desk/crm" data-testid="crm-back" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4"><ArrowLeft size={15} /> All contacts</Link>
      <div className="flex flex-wrap items-center gap-4 mb-5">
        <span className="w-14 h-14 rounded-2xl text-white flex items-center justify-center text-xl font-bold font-heading" style={{ background: "var(--brand-primary)" }}>
          {c.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
        </span>
        <div className="flex-1 min-w-[200px]">
          <h1 className="text-2xl font-bold font-heading" data-testid="crm-contact-name">{c.name}</h1>
          <p className="text-sm text-slate-500">{[c.job_title, c.organization].filter(Boolean).join(" · ") || c.email}</p>
          {c.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="crm-contact-tags">
              {c.tags.map((t) => <span key={t} className="text-[10px] font-semibold bg-teal-50 text-teal-800 border border-teal-200 rounded-full px-2 py-0.5">{t}</span>)}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button data-testid="crm-contact-edit" onClick={() => setEditor({ ...c })}
            className="inline-flex items-center gap-1.5 px-4 py-2 border border-neutral-300 rounded-lg text-sm font-medium hover:bg-neutral-50"><Pencil size={13} /> Edit</button>
          {isAdmin && (
            <button data-testid="crm-contact-delete" onClick={removeContact}
              className="p-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50"><Trash2 size={14} /></button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <StatCard icon={Ticket} label="Total tickets" value={c.stats.total_tickets} tone="slate" testId="crm-stat-total" />
        <StatCard icon={CircleDot} label="Open tickets" value={c.stats.open_tickets} tone={c.stats.open_tickets > 0 ? "amber" : "emerald"} testId="crm-stat-open" />
        <StatCard icon={CalendarClock} label="Contact since" value={new Date(c.created_at).toLocaleDateString()} tone="cyan" testId="crm-stat-since" />
        <StatCard icon={CalendarClock} label="Last activity" value={new Date(c.last_activity_at).toLocaleDateString()} tone="slate" testId="crm-stat-activity" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-5 h-fit" data-testid="crm-profile-card">
          <h3 className="font-semibold font-heading text-sm mb-2">Profile</h3>
          <Row icon={Mail} label="Email" value={c.email} />
          <Row icon={Phone} label="Phone" value={c.phone} />
          <Row icon={Building2} label="Organization" value={c.organization} />
          <Row icon={Briefcase} label="Job title" value={c.job_title} />
          <Row icon={MapPin} label="Address" value={c.address} />
          {c.notes && <p className="text-sm bg-amber-50 border border-amber-200 rounded-md p-3 mt-3 whitespace-pre-wrap" data-testid="crm-contact-notes">{c.notes}</p>}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5 h-fit" data-testid="crm-tickets-card">
          <h3 className="font-semibold font-heading text-sm mb-3">Tickets ({c.tickets.length})</h3>
          {c.tickets.length === 0 && <p className="text-xs text-slate-400">No tickets yet.</p>}
          <ul className="space-y-2.5">
            {c.tickets.map((t) => (
              <li key={t.id} className="text-sm">
                <div className="flex items-center gap-2">
                  <Link to={`/desk/tickets/${t.id}`} data-testid={`crm-ticket-${t.id}`} className="font-semibold text-sky-700 hover:underline">{t.number}</Link>
                  <Badge value={t.priority} map={PRIORITY_STYLE} />
                  <Badge value={t.status} map={STATUS_STYLE} />
                </div>
                <p className="text-xs text-slate-500 truncate mt-0.5">{t.title} · {new Date(t.created_at).toLocaleDateString()}</p>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5 h-fit" data-testid="crm-interactions-card">
          <h3 className="font-semibold font-heading text-sm mb-3">Activity log</h3>
          <form onSubmit={addInteraction} className="mb-4">
            <div className="flex gap-2">
              <select data-testid="crm-interaction-type" value={note.type} onChange={(e) => setNote((n) => ({ ...n, type: e.target.value }))} className={inputCls}>
                <option value="note">Note</option><option value="call">Call</option><option value="email">Email</option><option value="meeting">Meeting</option>
              </select>
              <input data-testid="crm-interaction-body" required placeholder="Log a note, call or email..." value={note.body}
                onChange={(e) => setNote((n) => ({ ...n, body: e.target.value }))} className={inputCls + " flex-1"} />
            </div>
            <button data-testid="crm-interaction-add" className="mt-2 px-4 py-1.5 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-xs font-medium">Log activity</button>
          </form>
          {c.interactions.length === 0 && <p className="text-xs text-slate-400">No activity logged yet.</p>}
          <ul className="space-y-3">
            {c.interactions.map((i) => {
              const Icon = I_ICON[i.type] || StickyNote;
              return (
                <li key={i.id} data-testid={`crm-interaction-${i.id}`} className="flex items-start gap-2.5 group">
                  <span className="w-7 h-7 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center shrink-0"><Icon size={13} /></span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm whitespace-pre-wrap">{i.body}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">{i.type} · {i.by} · {new Date(i.created_at).toLocaleString()}</p>
                  </div>
                  <button onClick={() => removeInteraction(i)} title="Delete entry"
                    className="opacity-0 group-hover:opacity-100 p-1 text-red-400 hover:text-red-600"><Trash2 size={12} /></button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {editor && <ContactEditor editor={editor} setEditor={setEditor} onSaved={() => { setEditor(null); load(); }} />}
    </div>
  );
}
